<script setup lang="ts">
import { computed, inject, type ComputedRef } from 'vue'
import { formatDateDetailed } from '@lib/util/date'
import useThemeVars from '@lib/composables/useThemeVars'
import useFolderNameDescription from '@lib/composables/useFolderNameDescription'
import useApiStore from '@browser/stores/api'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'

import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const { state } = useApiStore()
const { parseNameOnly } = useFolderNameDescription()

const themeVars = useThemeVars()

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const isDetailedDateEnabled = computed(() => {
  return launchDarkly.value?.enable_detailed_date_format
})

const path = state.selectedFolder?.path
  .map((record) =>
    record.name === 'root' ? t('canvasUI.browserApp.sidebar.contentSelection.allContent') : parseNameOnly(record.name),
  )
  .join(' > ')

const items = (state.selectedFolder?.folders?.length ?? 0) + (state.selectedFolder?.files?.length ?? 0)
</script>

<template>
  <div class="file-details w-full">
    <div class="flex mb-[10px] mt-[34px] font-600">
      <div>
        {{ t('canvasUI.browserApp.folderSelectionInfo.sections.details.title') }}
      </div>
    </div>

    <div class="flex flex-col gap-2 mb-4">
      <div class="title">
        {{ t('canvasUI.browserApp.folderSelectionInfo.sections.details.location') }}
      </div>
      <div>{{ path }}</div>
    </div>

    <div class="flex gap-8 lg:gap-16">
      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-2">
          <div class="title">
            {{ t('canvasUI.browserApp.folderSelectionInfo.sections.details.owner') }}
          </div>
          <div v-if="state.selectedFolder?.created_by?.name">
            {{ state.selectedFolder.created_by.name }}
          </div>
          <div v-else class="not-available">
            {{ t('canvasUI.browserApp.folderSelectionInfo.sections.details.notAvailable') }}
          </div>
        </div>

        <div class="flex flex-col gap-2">
          <div class="title">
            {{ t('canvasUI.browserApp.folderSelectionInfo.sections.details.lastUpdate') }}
          </div>
          <div
            v-if="state.selectedFolder?.modified_at"
            v-html="formatDateDetailed(state.selectedFolder?.modified_at, isDetailedDateEnabled)"
          ></div>
          <div v-else class="not-available">
            {{ t('canvasUI.browserApp.folderSelectionInfo.sections.details.notAvailable') }}
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-2">
          <div class="title">
            {{ t('canvasUI.browserApp.folderSelectionInfo.sections.details.items') }}
          </div>
          <div>
            {{ items }}
          </div>
        </div>

        <div class="flex flex-col gap-2">
          <div class="title">
            {{ t('canvasUI.browserApp.folderSelectionInfo.sections.details.createdOn') }}
          </div>
          <div
            v-if="state.selectedFolder?.created_at"
            v-html="formatDateDetailed(state.selectedFolder?.created_at, isDetailedDateEnabled)"
          ></div>
          <div v-else class="not-available">
            {{ t('canvasUI.browserApp.folderSelectionInfo.sections.details.notAvailable') }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.title {
  color: v-bind('themeVars.text2');
  font-style: normal;
  font-size: 15px;
  line-height: 18px;
  letter-spacing: 0.15px;
}

.not-available {
  color: v-bind('themeVars.text3');
}
</style>
